import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ChartBar, LockKeyOpen, Pulse, RadioButton } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { NewsroomAdminNav } from "@/components/NewsroomAdminNav";
import { Button } from "@/components/ui/button";
import { usePageSeo } from "@/hooks/usePageSeo";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const investigationStatuses = ["new", "triaged", "verifying", "follow-up-needed", "pursuing", "resolved", "closed"];

const formatDateTime = (value) => {
  if (!value) {
    return "No timestamp";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

const MetricCard = ({ icon, label, value, testId }) => (
  <article className="border border-[#D9DCE1] bg-[#F8F9FA] p-4" data-testid={testId}>
    <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">
      {icon}
      {label}
    </div>
    <p className="mt-3 font-[Bebas_Neue] text-4xl uppercase tracking-[0.04em] text-[#0A0A0A]">{value}</p>
  </article>
);

const BucketList = ({ items, title, testId }) => (
  <div className="border border-[#D9DCE1] bg-white p-5" data-testid={testId}>
    <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#1338BE]">{title}</p>
    <div className="mt-4 space-y-3">
      {items.map((item) => (
        <div key={`${title}-${item.label}`} className="flex items-center justify-between gap-4 border border-[#D9DCE1] bg-[#F8F9FA] px-4 py-3" data-testid={`${testId}-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
          <span className="text-sm font-semibold text-slate-700">{item.label}</span>
          <span className="text-sm font-bold uppercase tracking-[0.14em] text-[#0A0A0A]">{item.value}</span>
        </div>
      ))}
    </div>
  </div>
);

export const AdminSubmissionsPage = () => {
  usePageSeo({ canonicalPath: "/admin/submissions", description: "Review submissions, investigative portal threads, and newsroom analytics.", robots: "noindex,nofollow", title: "Newsroom Intake + Analytics | Content Watch HQ" });
  const navigate = useNavigate();
  const [token] = useState(() => window.localStorage.getItem("content_watch_token"));
  const [submissions, setSubmissions] = useState([]);
  const [investigations, setInvestigations] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeInvestigationId, setActiveInvestigationId] = useState(null);
  const [draftStatus, setDraftStatus] = useState("new");
  const [assignedTo, setAssignedTo] = useState("");
  const [newsroomMessage, setNewsroomMessage] = useState("");
  const [internalNote, setInternalNote] = useState("");
  const [savingInvestigation, setSavingInvestigation] = useState(false);

  const loadDashboard = async () => {
    const headers = { Authorization: `Bearer ${token}` };
    const [submissionResponse, investigationResponse, analyticsResponse] = await Promise.all([
      axios.get(`${API_BASE}/office/submissions`, { headers }),
      axios.get(`${API_BASE}/office/investigations`, { headers }),
      axios.get(`${API_BASE}/office/analytics`, { headers }),
    ]);
    setSubmissions(submissionResponse.data);
    setInvestigations(investigationResponse.data);
    setAnalytics(analyticsResponse.data);
  };

  useEffect(() => {
    if (!token) {
      navigate("/newsroom-login");
      return;
    }
    axios.get(`${API_BASE}/auth/me`, { headers: { Authorization: `Bearer ${token}` } }).then(() => loadDashboard()).catch(() => {
      window.localStorage.removeItem("content_watch_token");
      navigate("/newsroom-login");
    }).finally(() => setLoading(false));
  }, [navigate, token]);

  const updateStatus = async (submissionId, status) => {
    try {
      await axios.patch(`${API_BASE}/office/submissions/${submissionId}`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Submission updated.");
      await loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Status update failed.");
    }
  };

  const activeInvestigation = useMemo(() => investigations.find((item) => item.id === activeInvestigationId) || null, [activeInvestigationId, investigations]);

  useEffect(() => {
    if (activeInvestigation) {
      setDraftStatus(activeInvestigation.status || "new");
      setAssignedTo(activeInvestigation.assigned_to || "");
      return;
    }
    setDraftStatus("new");
    setAssignedTo("");
  }, [activeInvestigation]);

  const saveInvestigation = async (event) => {
    event.preventDefault();
    if (!activeInvestigation) {
      return;
    }
    setSavingInvestigation(true);
    try {
      await axios.patch(`${API_BASE}/office/investigations/${activeInvestigation.id}`, {
        status: draftStatus,
        assigned_to: assignedTo,
        newsroom_message: newsroomMessage,
        internal_note: internalNote,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Investigation case updated.");
      setNewsroomMessage("");
      setInternalNote("");
      await loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Investigation update failed.");
    } finally {
      setSavingInvestigation(false);
    }
  };

  const visibleGeneralSubmissions = submissions.filter((item) => item.intake_mode !== "investigative");

  return (
    <main className="page-shell mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-14 lg:px-8 lg:py-16" data-testid="admin-submissions-page">
      <NewsroomAdminNav />

      <section className="border border-[#D9DCE1] bg-white p-6 shadow-[0_20px_60px_rgba(15,23,42,0.04)] sm:p-8" data-testid="admin-submissions-hero-panel">
        <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#D21404]">Newsroom intake control</p>
        <h1 className="mt-3 font-[Bebas_Neue] text-5xl uppercase tracking-[0.04em] text-[#0A0A0A]">Submissions, investigations, and analytics in one room</h1>
        <p className="mt-4 max-w-3xl text-base leading-8 text-slate-700">Review contact intake, watch investigation threads, and keep the newsroom pointed at the right desks with live analytics.</p>
      </section>

      <section className="mt-8 grid gap-4 sm:grid-cols-2 xl:grid-cols-4" data-testid="admin-submissions-metric-grid">
        {(analytics?.summary_cards || []).slice(0, 4).map((card) => (
          <MetricCard key={card.label} icon={<ChartBar size={16} weight="fill" />} label={card.label} testId={`admin-metric-card-${card.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} value={card.value} />
        ))}
      </section>

      <section className="mt-8 grid gap-8 xl:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]" data-testid="admin-submissions-main-grid">
        <div className="space-y-8">
          <section className="border border-[#D9DCE1] bg-white p-6 shadow-[0_20px_60px_rgba(15,23,42,0.04)] sm:p-8" data-testid="admin-investigations-panel">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#D21404]">Investigative portal</p>
                <h2 className="mt-3 font-[Bebas_Neue] text-4xl uppercase tracking-[0.04em] text-[#0A0A0A]">Locked-down whistleblower cases</h2>
              </div>
              <Button className="rounded-none border border-[#0A0A0A] bg-transparent px-4 py-3 text-xs font-bold uppercase tracking-[0.18em] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white" data-testid="admin-submissions-refresh-button" onClick={() => loadDashboard()} type="button" variant="ghost">Refresh room</Button>
            </div>

            <div className="mt-8 grid gap-6 lg:grid-cols-[320px_minmax(0,1fr)]">
              <div className="space-y-4" data-testid="admin-investigations-list">
                {investigations.map((investigation) => (
                  <button key={investigation.id} className={`w-full border p-4 text-left transition-colors ${activeInvestigationId === investigation.id ? "border-[#1338BE] bg-[rgba(19,56,190,0.05)]" : "border-[#D9DCE1] bg-[#F8F9FA] hover:border-[#1338BE]"}`} data-testid={`admin-investigation-card-${investigation.id}`} onClick={() => setActiveInvestigationId(investigation.id)} type="button">
                    <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#1338BE]">{investigation.tracking_id}</p>
                    <h3 className="mt-2 text-sm font-bold uppercase tracking-[0.08em] text-[#0A0A0A]">{investigation.tip_category || "Investigative case"}</h3>
                    <p className="mt-2 text-sm text-slate-600">{investigation.involved_entity || investigation.incident_location || "Source withheld"}</p>
                    <div className="mt-3 flex items-center justify-between gap-3 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">
                      <span>{investigation.status}</span>
                      <span>{investigation.message_count} msgs</span>
                    </div>
                  </button>
                ))}
              </div>

              <div className="border border-[#D9DCE1] bg-[#F8F9FA] p-5" data-testid="admin-investigation-detail-panel">
                {activeInvestigation ? (
                  <>
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div>
                        <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#D21404]">Case detail</p>
                        <h3 className="mt-2 font-[Bebas_Neue] text-4xl uppercase tracking-[0.04em] text-[#0A0A0A]" data-testid="admin-investigation-detail-title">{activeInvestigation.tracking_id}</h3>
                      </div>
                      <div className="text-right text-xs uppercase tracking-[0.16em] text-slate-500" data-testid="admin-investigation-last-updated">
                        <p>Last updated</p>
                        <p className="mt-1 font-bold text-[#0A0A0A]">{formatDateTime(activeInvestigation.updated_at)}</p>
                      </div>
                    </div>

                    <div className="mt-5 grid gap-3 sm:grid-cols-2" data-testid="admin-investigation-meta-grid">
                      <div className="border border-[#D9DCE1] bg-white p-4"><p className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">Source relationship</p><p className="mt-2 text-sm font-semibold text-slate-900">{activeInvestigation.source_relationship || "Unknown"}</p></div>
                      <div className="border border-[#D9DCE1] bg-white p-4"><p className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">Sensitivity</p><p className="mt-2 text-sm font-semibold text-slate-900">{activeInvestigation.sensitivity_level || "Standard"}</p></div>
                      <div className="border border-[#D9DCE1] bg-white p-4"><p className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">Entity</p><p className="mt-2 text-sm font-semibold text-slate-900">{activeInvestigation.involved_entity || "Not supplied"}</p></div>
                      <div className="border border-[#D9DCE1] bg-white p-4"><p className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">Location</p><p className="mt-2 text-sm font-semibold text-slate-900">{activeInvestigation.incident_location || "Not supplied"}</p></div>
                    </div>

                    <div className="mt-6 space-y-4" data-testid="admin-investigation-thread-list">
                      {activeInvestigation.portal_messages.map((message, index) => (
                        <article key={message.id || `${message.sender}-${index}`} className={`border p-4 ${message.sender === "newsroom" ? "border-[rgba(19,56,190,0.16)] bg-[rgba(19,56,190,0.05)]" : "border-[#D9DCE1] bg-white"}`} data-testid={`admin-investigation-thread-item-${index}`}>
                          <div className="flex flex-wrap items-center justify-between gap-3">
                            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">{message.sender === "newsroom" ? "Newsroom reply" : "Source message"}</p>
                            <p className="text-[11px] uppercase tracking-[0.16em] text-slate-500">{formatDateTime(message.created_at)}</p>
                          </div>
                          <p className="mt-3 whitespace-pre-line text-sm leading-7 text-slate-700">{message.body}</p>
                          {message.file_urls?.length ? (
                            <div className="mt-4 flex flex-wrap gap-2" data-testid={`admin-investigation-thread-files-${index}`}>
                              {message.file_urls.map((fileUrl, fileIndex) => (
                                <a key={`${message.id}-${fileUrl}`} className="border border-[#D9DCE1] bg-[#F8F9FA] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-[#1338BE] transition-colors hover:bg-[#1338BE] hover:text-white" data-testid={`admin-investigation-thread-file-${index}-${fileIndex}`} href={fileUrl} rel="noreferrer" target="_blank">
                                  {message.file_names?.[fileIndex] || `File ${fileIndex + 1}`}
                                </a>
                              ))}
                            </div>
                          ) : null}
                        </article>
                      ))}
                    </div>

                    <form className="mt-6 grid gap-4 border-t border-[#D9DCE1] pt-6" data-testid="admin-investigation-update-form" onSubmit={saveInvestigation}>
                      <div className="grid gap-4 sm:grid-cols-2">
                        <select className="border border-[#D9DCE1] bg-white px-4 py-4 text-sm outline-none focus:border-[#1338BE]" data-testid="admin-investigation-status-select" onChange={(event) => setDraftStatus(event.target.value)} value={draftStatus}>
                          {investigationStatuses.map((status) => <option key={status} value={status}>{status}</option>)}
                        </select>
                        <input className="border border-[#D9DCE1] bg-white px-4 py-4 text-sm outline-none focus:border-[#1338BE]" data-testid="admin-investigation-assigned-to-input" onChange={(event) => setAssignedTo(event.target.value)} placeholder="Assigned reporter or producer" type="text" value={assignedTo} />
                      </div>
                      <textarea className="min-h-[8rem] border border-[#D9DCE1] bg-white px-4 py-4 text-sm outline-none focus:border-[#1338BE]" data-testid="admin-investigation-newsroom-message-textarea" onChange={(event) => setNewsroomMessage(event.target.value)} placeholder="Message back to the source through the portal" value={newsroomMessage} />
                      <textarea className="min-h-[7rem] border border-[#D9DCE1] bg-white px-4 py-4 text-sm outline-none focus:border-[#1338BE]" data-testid="admin-investigation-internal-note-textarea" onChange={(event) => setInternalNote(event.target.value)} placeholder="Internal note for the newsroom only" value={internalNote} />
                      <Button className="rounded-none bg-[#1338BE] px-5 py-6 text-xs font-bold uppercase tracking-[0.2em] text-white hover:bg-[#102F9B]" data-testid="admin-investigation-save-button" disabled={savingInvestigation} type="submit">
                        {savingInvestigation ? "Saving Case" : "Save Investigation Update"}
                      </Button>
                    </form>
                  </>
                ) : (
                  <div className="flex min-h-[320px] items-center justify-center text-center" data-testid="admin-investigation-empty-state">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#D21404]">No case selected</p>
                      <p className="mt-3 text-sm leading-7 text-slate-700">Choose an investigative case from the left column to review the thread, set status, and respond securely.</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </section>

          <section className="border border-[#D9DCE1] bg-white p-6 shadow-[0_20px_60px_rgba(15,23,42,0.04)] sm:p-8" data-testid="admin-general-submissions-panel">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#D21404]">Standard intake</p>
            <h2 className="mt-3 font-[Bebas_Neue] text-4xl uppercase tracking-[0.04em] text-[#0A0A0A]">Contact, newsletter, and non-portal tips</h2>
            <div className="mt-8 space-y-5" data-testid="admin-submissions-list">
              {visibleGeneralSubmissions.map((submission) => (
                <article key={submission.id} className="border border-[#D9DCE1] bg-[#F8F9FA] p-5 sm:p-6" data-testid={`admin-submission-card-${submission.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#1338BE]">{submission.submission_type}</p>
                      <h3 className="mt-2 text-lg font-bold uppercase tracking-[0.08em] text-[#0A0A0A]">{submission.name || `${submission.first_name || ""} ${submission.last_name || ""}`.trim() || submission.email || "Anonymous source"}</h3>
                    </div>
                    <select className="border border-[#D9DCE1] bg-white px-3 py-2 text-xs font-bold uppercase tracking-[0.18em] text-slate-700" data-testid={`admin-submission-status-${submission.id}`} onChange={(event) => updateStatus(submission.id, event.target.value)} value={submission.status}>
                      <option value="new">New</option>
                      <option value="reviewing">Reviewing</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>
                  <p className="mt-3 text-sm leading-7 text-slate-700">{submission.details || submission.reason || "No additional details supplied."}</p>
                  {submission.file_urls?.length ? (
                    <div className="mt-4 flex flex-wrap gap-2" data-testid={`admin-submission-files-${submission.id}`}>
                      {submission.file_urls.map((fileUrl, index) => (
                        <a key={`${submission.id}-${fileUrl}`} className="border border-[#D9DCE1] bg-white px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-[#1338BE] transition-colors hover:bg-[#1338BE] hover:text-white" data-testid={`admin-submission-file-${submission.id}-${index}`} href={fileUrl} rel="noreferrer" target="_blank">
                          {submission.file_names?.[index] || `Attachment ${index + 1}`}
                        </a>
                      ))}
                    </div>
                  ) : null}
                  <p className="mt-4 text-xs uppercase tracking-[0.16em] text-slate-500">{submission.email || submission.phone || "No contact info provided"}</p>
                </article>
              ))}
            </div>
          </section>
        </div>

        <aside className="space-y-6" data-testid="admin-analytics-sidebar">
          {loading ? <div className="border border-[#D9DCE1] bg-white p-6 text-sm text-slate-600">Loading newsroom analytics…</div> : null}
          {analytics ? (
            <>
              <BucketList items={analytics.desk_mix || []} testId="admin-analytics-desk-mix" title="Desk mix" />
              <BucketList items={analytics.investigation_statuses || []} testId="admin-analytics-investigation-statuses" title="Investigation statuses" />
              <BucketList items={analytics.entity_watch || []} testId="admin-analytics-entity-watch" title="Entity watch" />

              <div className="border border-[#D9DCE1] bg-white p-5" data-testid="admin-analytics-recent-updates">
                <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] text-[#D21404]"><Pulse size={16} weight="fill" /> Recent story updates</p>
                <div className="mt-4 space-y-3">
                  {(analytics.recent_story_updates || []).map((story) => (
                    <div key={story.slug} className="border border-[#D9DCE1] bg-[#F8F9FA] px-4 py-3" data-testid={`admin-analytics-recent-story-${story.slug}`}>
                      <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#1338BE]">{story.section}</p>
                      <p className="mt-2 text-sm font-semibold text-slate-900">{story.headline}</p>
                      <p className="mt-2 text-xs uppercase tracking-[0.16em] text-slate-500">{formatDateTime(story.updated_at)}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border border-[#D9DCE1] bg-white p-5" data-testid="admin-analytics-secondary-metrics">
                <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] text-[#1338BE]"><RadioButton size={16} weight="fill" /> Livestream + sponsor watch</p>
                <div className="mt-4 space-y-3">
                  {[...(analytics.livestream_statuses || []), ...(analytics.sponsor_metrics || [])].map((item) => (
                    <div key={`${item.label}-${item.value}`} className="flex items-center justify-between gap-4 border border-[#D9DCE1] bg-[#F8F9FA] px-4 py-3" data-testid={`admin-analytics-secondary-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      <span className="text-sm font-semibold text-slate-700">{item.label}</span>
                      <span className="text-sm font-bold uppercase tracking-[0.14em] text-[#0A0A0A]">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border border-[#D9DCE1] bg-white p-5" data-testid="admin-analytics-top-authors">
                <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] text-[#1338BE]"><LockKeyOpen size={16} weight="fill" /> Output leaders</p>
                <div className="mt-4 space-y-3">
                  {(analytics.top_authors || []).map((item) => (
                    <div key={item.label} className="flex items-center justify-between gap-4 border border-[#D9DCE1] bg-[#F8F9FA] px-4 py-3" data-testid={`admin-top-author-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      <span className="text-sm font-semibold text-slate-700">{item.label}</span>
                      <span className="text-sm font-bold uppercase tracking-[0.14em] text-[#0A0A0A]">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : null}
        </aside>
      </section>
    </main>
  );
};